import { useCallback, useEffect, useRef, useState } from 'react';

import { TextEditorEditParams } from '@dreryk/react-components';
import { getTextEditorPlainTextValue, TextEditorValue } from '@dreryk/react-text-editor';

import { useTranscriptionRedaction } from '../transcription/useTranscriptionRedaction';
import { Field, TextRedactionResult } from '../types';

import { TextEditorFieldAccess } from './textEditorField.types';
import { buildRedactedHtml } from './textEditorField.utils';

export type UseTextEditorFieldAiEditOptions = TextEditorFieldAccess & {
   field: Field;
};

export type UseTextEditorFieldAiEditResult = {
   handleEdit: (params: TextEditorEditParams) => Promise<boolean>;
   handleAccept: VoidFunction;
   handleReject: VoidFunction;
   isProcessing: boolean;
   isPendingDecision: boolean;
};

// eslint-disable-next-line max-lines-per-function, max-statements
export const useTextEditorFieldAiEdit = (options: UseTextEditorFieldAiEditOptions): UseTextEditorFieldAiEditResult => {
   const [isProcessing, setIsProcessing] = useState(false);
   const [isPendingDecision, setIsPendingDecision] = useState(false);
   const isSelectionRef = useRef(false);
   const redactedTextRef = useRef('');
   const selectedTextRef = useRef('');
   const selectionStartRef = useRef<number | null>(null);
   const originalHtmlRef = useRef('');
   const resolveRef = useRef<((processed: boolean) => void) | null>(null);
   const optionsRef = useRef(options);
   optionsRef.current = options;

   const finishPending = useCallback((processed: boolean) => {
      resolveRef.current?.(processed);
      resolveRef.current = null;
      setIsProcessing(false);
   }, []);

   const redaction = useTranscriptionRedaction({
      onSuccess: (result: TextRedactionResult) => {
         redactedTextRef.current = result.redactedText;

         optionsRef.current.setHtml(
            buildRedactedHtml({
               originalHtml: originalHtmlRef.current,
               selectedText: selectedTextRef.current,
               selectionStart: selectionStartRef.current,
               isSelection: isSelectionRef.current,
               redactedText: result.redactedText,
               highlighted: true,
            })
         );

         setIsPendingDecision(true);
         finishPending(true);
      },
   });
   const redactionRef = useRef(redaction);
   redactionRef.current = redaction;

   useEffect(() => {
      if (redaction.error && isProcessing) {
         finishPending(false);
      }
   }, [redaction.error, isProcessing, finishPending]);

   const handleEdit = useCallback(async (params: TextEditorEditParams): Promise<boolean> => {
      const html = optionsRef.current.getHtml();
      const isSelection = params.isSelection && params.selectedText.trim().length > 0;
      const text = isSelection ? params.selectedText : getTextEditorPlainTextValue(TextEditorValue.fromFormatted(html));

      if (!text.trim()) {
         return false;
      }

      isSelectionRef.current = isSelection;
      selectedTextRef.current = isSelection ? params.selectedText : '';
      selectionStartRef.current = isSelection ? params.selectionStart : null;
      originalHtmlRef.current = html;
      redactedTextRef.current = '';
      setIsProcessing(true);

      return new Promise<boolean>((resolve) => {
         resolveRef.current = resolve;
         redactionRef.current.execute({ field: optionsRef.current.field, text: text });
      });
   }, []);

   const clearPending = useCallback(() => {
      redactedTextRef.current = '';
      originalHtmlRef.current = '';
      selectedTextRef.current = '';
      selectionStartRef.current = null;
      setIsPendingDecision(false);
   }, []);

   const handleAccept = useCallback(() => {
      if (redactedTextRef.current) {
         optionsRef.current.setHtml(
            buildRedactedHtml({
               originalHtml: originalHtmlRef.current,
               selectedText: selectedTextRef.current,
               selectionStart: selectionStartRef.current,
               isSelection: isSelectionRef.current,
               redactedText: redactedTextRef.current,
               highlighted: false,
            })
         );
      }
      clearPending();
   }, [clearPending]);

   const handleReject = useCallback(() => {
      optionsRef.current.setHtml(originalHtmlRef.current);
      clearPending();
   }, [clearPending]);

   return {
      handleEdit: handleEdit,
      handleAccept: handleAccept,
      handleReject: handleReject,
      isProcessing: isProcessing,
      isPendingDecision: isPendingDecision,
   };
};
